import {Record} from 'immutable';
import i18next from 'i18next';
import {type ColorKey, type IconName, type IconProps} from 'modern-famly';

import {hasValue} from 'web-app/util/typescript';

export class Role extends Record<{
    roleId: string;
    title: string;
    description?: string;
}>({
    roleId: '',
    title: '',
    description: undefined,
}) {}

const fromAPI = data => {
    return new Role({
        roleId: data.roleId,
        title: data.title,
        description: data.description,
    });
};

export default {
    record: Role,
    default: new Role(),
    fromAPI,
};

export const NO_LOGIN_ROLE_MARKER = 'no-login-role';
export const PIN_CODE_CHECKIN_PICKUP_ROLE_ID = 'db8bc92d-937f-4250-84e8-7559952e7bab';
export const PARENT_ROLE_ID = 'd27c12e8-e1bf-4cb6-b10e-def7a83e3350';
export const FAMILY_ROLE_ID = '2a8c1370-3ea7-40e8-9a60-8773bbe66c5e';
export const FAMILY_WITH_INFO_ROLE_ID = 'e98bb80d-8127-42b7-a108-eb17e1adadd8';

export const roleIdToTitle = (roleId: string | undefined) => {
    switch (roleId) {
        case PARENT_ROLE_ID:
            return i18next.t('childProfile.parent');
        case PIN_CODE_CHECKIN_PICKUP_ROLE_ID:
            return i18next.t('childProfile.pickup');
        case FAMILY_ROLE_ID:
            return i18next.t('childProfile.family');
        case FAMILY_WITH_INFO_ROLE_ID:
            return i18next.t('childProfile.familyWithInfo');
        default:
            return '';
    }
};

export const roleIdToModernFamlyIcon = (roleId: string | ''): IconProps => {
    switch (roleId) {
        case PARENT_ROLE_ID:
            return {name: 'person' as IconName, color: 'g400' as ColorKey, filled: true};
        case PIN_CODE_CHECKIN_PICKUP_ROLE_ID:
            return {name: 'password'};
        case FAMILY_ROLE_ID:
            return {name: 'diversity_1' as IconName, color: 'p300' as ColorKey};
        case FAMILY_WITH_INFO_ROLE_ID:
            return {name: 'no_accounts' as IconName};
        case NO_LOGIN_ROLE_MARKER:
        default:
            return {name: 'no_accounts' as IconName};
    }
};

export const roleIdToModel = (roleId: string | undefined) => {
    switch (roleId) {
        case PARENT_ROLE_ID:
            return fromAPI({
                roleId: PARENT_ROLE_ID,
                title: roleIdToTitle(PARENT_ROLE_ID),
                description: i18next.t('childProfile.parentRoleDescription'),
            });
        case PIN_CODE_CHECKIN_PICKUP_ROLE_ID:
            return fromAPI({
                roleId: PIN_CODE_CHECKIN_PICKUP_ROLE_ID,
                title: roleIdToTitle(PIN_CODE_CHECKIN_PICKUP_ROLE_ID),
                description: i18next.t('childProfile.pickuproleDescription'),
            });
        case FAMILY_ROLE_ID:
            return fromAPI({
                roleId: FAMILY_ROLE_ID,
                title: roleIdToTitle(FAMILY_ROLE_ID),
                description: i18next.t('childProfile.familyRoleDescription'),
            });
        default:
            return undefined;
    }
};

export const childRelationRoles = (allowedRoles: string[] | null, usePincodeCheckin: boolean) => {
    const roles = [roleIdToModel(PARENT_ROLE_ID), roleIdToModel(FAMILY_ROLE_ID)];

    if (usePincodeCheckin) {
        roles.push(roleIdToModel(PIN_CODE_CHECKIN_PICKUP_ROLE_ID));
    }

    if (hasValue(allowedRoles)) {
        return roles.filter(hasValue).filter(role => role && allowedRoles.includes(role.get('roleId', '')));
    }

    return roles.filter(hasValue);
};
